// Fuente ÚNICA de verdad para la detección "es Nota de Crédito" y su signo de neteo (regla 10.1).
//
// Antes esto vivía duplicado inline en ~20 sitios financieros (IVA, ledgers, ER, por-pagar):
// algunos comparaban `f.tipo_dte === 61` (crudo, asume número) y otros `Number(f.tipo_dte) === 61`
// (defiende un eventual string). Hoy la columna `tipo_dte` es INTEGER en ambas tablas SII
// (`facturas_sii_emitidas` / `facturas_sii_recibidas`), así que supabase-js siempre la devuelve
// como número y las dos formas dan idéntico resultado. Se canoniza la variante con `Number(...)`
// por defensa en profundidad (regla 10.177 — prohibido el fallo silencioso): una NC no detectada
// por diferencia de tipo entraría con el signo equivocado a un saldo.
//
// ALCANCE: SOLO la Nota de Crédito nacional (DTE 61). NO incluye 56 (Nota de DÉBITO — signo +1,
// lo contrario de una NC) ni 111 (ND exportación, +1). El único otro código que sería NC de plata
// es 112 (NC de exportación): hoy hay 0 filas de 112 en la base, por eso no se agrega todavía;
// si algún día aparece un caso real, se suma acá y `signoNC` lo netearía con −1 automáticamente.
// El grupo [56, 61, 111, 112] de ConciliacionSII es OTRO predicado (esNotaDeAjuste, agrupación de
// UI para el tab de notas), NO este — nunca lo uses para signo/neteo.

export const TIPO_DTE_NOTA_CREDITO = 61;

// True si el documento es una Nota de Crédito (resta plata). Acepta cualquier fila con `tipo_dte`.
export const esNotaCredito = (f) => Number(f?.tipo_dte) === TIPO_DTE_NOTA_CREDITO;

// Signo de neteo contable: −1 para NC (viene guardada en positivo, se le invierte el signo para
// que reduzca en vez de inflar), +1 para todo lo demás.
export const signoNC = (f) => (esNotaCredito(f) ? -1 : 1);

// ─── Etiquetas legibles de los códigos DTE (fuente única, regla 10.1) ─────────
// Antes vivían duplicadas en DashboardIva y ConciliacionSII con dos problemas: no coincidían
// entre sí (33 "Factura afecta" vs "Factura", 61 "NC crédito" vs "Nota crédito") y AMBAS tenían
// mal los códigos de exportación. Se corrigen al estándar SII real:
//   110 = Factura de exportación · 111 = Nota de DÉBITO de exportación · 112 = Nota de CRÉDITO de exportación.
// (DashboardIva tenía 111/112 invertidos; ConciliacionSII rotulaba 111 "Liquidación exp." y 112
// "Nota débito exp." — ambos incorrectos.)
export const TIPO_DTE_LABEL = {
  33: "Factura afecta",
  34: "Factura exenta",
  39: "Boleta",
  41: "Boleta exenta",
  46: "Factura de compra",
  56: "Nota de débito",
  61: "Nota de crédito",
  110: "Factura de exportación",
  111: "Nota de débito exp.",
  112: "Nota de crédito exp.",
};

// Etiqueta legible de un código DTE; fallback "DTE <n>" para códigos no mapeados.
export const tipoDteLabel = (tipo) => TIPO_DTE_LABEL[tipo] || `DTE ${tipo}`;

// Etiqueta CORTA de tipo de documento para la grilla de selección de DTE (SeleccionarDTEModal):
// "NC" (nota de crédito, vía esNotaCredito — nunca `=== 61` inline, 10.1), "Factura exportación"
// (110) o "Factura" (33/34 y cualquier otro afecto). La tabla emitidas hoy solo tiene 33/61/110.
export const tipoDocumentoCorto = (f) =>
  esNotaCredito(f) ? "NC" : Number(f?.tipo_dte) === 110 ? "Factura exportación" : "Factura";

// Grupo de "notas de ajuste" que corrigen/anulan otro documento (crédito Y débito, normales y
// de exportación). OJO: NO es lo mismo que esNotaCredito — incluye notas de DÉBITO (56/111), que
// tienen signo +1. Se usa SOLO para agrupación de UI (el tab de notas de ConciliacionSII), nunca
// para signo/neteo de plata.
export const esNotaDeAjuste = (f) => [56, 61, 111, 112].includes(Number(f?.tipo_dte));
